import { useState } from 'react';
import { Clock, Package, AlertTriangle, Mic, FileText } from 'lucide-react';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Spinner } from '@/shared/ui/Spinner';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/toast';
import { ApiException } from '@/shared/api/client';
import { formatMinutes, formatDateTime } from '@/lib/utils';
import { useFieldReport, useReviewFieldReport } from './field-reports.api';

export function FieldReportDetailDialog({
  reportId,
  onClose,
}: {
  reportId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useFieldReport(reportId);
  const review = useReviewFieldReport();
  const push = useToast((s) => s.push);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState('');

  const act = async (status: 'APPROVED' | 'REJECTED' | 'REVIEW_REQUIRED') => {
    if (!reportId) return;
    if (status === 'REJECTED' && !reason.trim()) {
      push('Bitte eine Begründung angeben – der Mitarbeiter muss wissen, was fehlt.', 'error');
      return;
    }
    try {
      await review.mutateAsync({ id: reportId, status, reason: reason.trim() || undefined });
      push(
        status === 'APPROVED'
          ? 'Meldung geprüft.'
          : status === 'REJECTED'
            ? 'Meldung abgelehnt.'
            : 'Zur Prüfung zurückgestellt.',
      );
      setRejectMode(false);
      setReason('');
      if (status !== 'REVIEW_REQUIRED') onClose();
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Aktion fehlgeschlagen.', 'error');
    }
  };

  const canDecide = data?.status === 'SUBMITTED' || data?.status === 'REVIEW_REQUIRED';
  const canReopen = data?.status === 'APPROVED' || data?.status === 'REJECTED';
  const photos = data?.media?.filter((m) => m.type === 'PHOTO') ?? [];
  const audios = data?.media?.filter((m) => m.type === 'AUDIO') ?? [];

  return (
    <Dialog open={!!reportId} onClose={onClose} title="Feldmeldung">
      {isLoading || !data ? (
        <Spinner />
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={data.status} />
            <span className="text-xs text-slate-500">{formatDateTime(data.submittedAt)}</span>
            {data.employee && (
              <span className="text-xs text-slate-500">von {data.employee.displayName}</span>
            )}
          </div>

          <div className="text-sm">
            <p className="font-medium text-slate-900">{data.project.title}</p>
            <p className="text-slate-500">
              {data.project.customer.companyName} · {data.project.object.name}
            </p>
          </div>

          {data.summary && (
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-600 sm:grid-cols-4">
              <span>⏱ {formatMinutes(data.summary.totalMinutes)}</span>
              <span>
                📷 {data.summary.photosBefore} vorher / {data.summary.photosAfter} nachher
              </span>
              <span>{data.summary.hasAudio ? '🎙 Audio' : '– kein Audio'}</span>
              <span>⚠ {data.summary.openExtraServices} Zusatz offen</span>
            </div>
          )}

          {photos.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-700">Fotos</h3>
              <div className="grid grid-cols-3 gap-2">
                {photos.map((m) => (
                  <a key={m.id} href={m.url} target="_blank" rel="noreferrer" className="relative block">
                    <img src={m.url} alt="" className="h-24 w-full rounded-lg object-cover" />
                    <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                      {m.phase === 'BEFORE' ? 'Vorher' : m.phase === 'AFTER' ? 'Nachher' : 'Sonst.'}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {audios.length > 0 && (
            <div>
              <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                <Mic className="h-4 w-4" /> Sprachaufnahmen
              </h3>
              {audios.map((m) => (
                <audio key={m.id} controls src={m.url} className="mb-2 w-full" />
              ))}
            </div>
          )}

          {data.noteText && (
            <div>
              <h3 className="mb-1 text-sm font-semibold text-slate-700">Notiz</h3>
              <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-800">{data.noteText}</p>
            </div>
          )}

          {data.transcript && (
            <div>
              <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                <FileText className="h-4 w-4" /> Transkript
              </h3>
              <p className="whitespace-pre-wrap rounded-lg bg-blue-50 p-3 text-sm text-slate-800">
                {data.transcript}
              </p>
            </div>
          )}

          {(data.workTimes?.length ?? 0) > 0 && (
            <div>
              <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                <Clock className="h-4 w-4" /> Arbeitszeiten
              </h3>
              <ul className="space-y-1 text-sm text-slate-800">
                {data.workTimes!.map((w) => (
                  <li key={w.id} className="flex justify-between rounded bg-slate-50 px-3 py-1.5">
                    <span>{w.note ?? 'Arbeitszeit'}</span>
                    <span className="font-medium">{formatMinutes(w.durationMinutes)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(data.materials?.length ?? 0) > 0 && (
            <div>
              <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                <Package className="h-4 w-4" /> Material
              </h3>
              <ul className="space-y-1 text-sm text-slate-800">
                {data.materials!.map((m) => (
                  <li key={m.id} className="flex justify-between rounded bg-slate-50 px-3 py-1.5">
                    <span>{m.name}</span>
                    <span className="font-medium">
                      {Number(m.quantity).toLocaleString('de-DE')} {m.unit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(data.extraServices?.length ?? 0) > 0 && (
            <div>
              <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-amber-700">
                <AlertTriangle className="h-4 w-4" /> Gemeldete Zusatzleistungen
              </h3>
              <ul className="space-y-1 text-sm">
                {data.extraServices!.map((e) => (
                  <li key={e.id} className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
                    <p className="font-medium text-slate-900">{e.title}</p>
                    {e.reason && <p className="text-xs text-slate-600">{e.reason}</p>}
                    <p className="mt-0.5 text-xs text-amber-700">
                      {e.confirmedInternal ? 'Intern bestätigt' : 'Noch nicht geprüft → Zusatzleistungen'}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {canDecide && !rejectMode && (
            <div className="flex gap-2 border-t border-slate-200 pt-4">
              <Button className="flex-1" onClick={() => act('APPROVED')} loading={review.isPending}>
                Geprüft
              </Button>
              <Button variant="danger" className="flex-1" onClick={() => setRejectMode(true)}>
                Ablehnen
              </Button>
            </div>
          )}
          {rejectMode && (
            <div className="space-y-2 border-t border-slate-200 pt-4">
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="Was fehlt? (z. B. Nachher-Foto fehlt, bitte nacherfassen)"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  className="flex-1"
                  onClick={() => act('REJECTED')}
                  loading={review.isPending}
                >
                  Ablehnung senden
                </Button>
                <Button variant="secondary" onClick={() => setRejectMode(false)}>
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
          {canReopen && (
            <div className="border-t border-slate-200 pt-4">
              <Button variant="secondary" onClick={() => act('REVIEW_REQUIRED')} loading={review.isPending}>
                Erneut prüfen
              </Button>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
